// API client for the coframe.author.http FastAPI backend.
//
// In dev: Vite proxies /api/* → http://localhost:8000/* (per vite.config.ts).
// In prod / standalone: set VITE_API_BASE_URL or pass baseUrl directly.

import type {
  Certificate,
  ColumnView,
  ConditionStatus,
  CreateSessionRequest,
  HealthResponse,
  InstallationsResponse,
  Level,
  OpCatalog,
  RunOpRequest,
  RunOpResponse,
  SessionInfo,
  SessionLog,
} from "./types";

/** Cached column-pool views for a session's scoped tables.
 *  ``cached`` is true when served from the session cache (no re-profiling);
 *  ``scope_key`` is the scope signature the views were computed for. */
export interface ColumnPoolResponse {
  columns: ColumnView[];
  cached: boolean;
  scope_key: string;
}

export interface ConditionsResponse {
  session_id: string;
  condition_statuses: Record<string, ConditionStatus>;
  /** The four-state certificate (source of truth). Optional so an older
   *  backend that only emits ``last_computed_level`` still type-checks. */
  certificate?: Certificate | null;
  last_computed_level: Level | null;
}

/** The input missingness categories an operator dispatches on
 *  (Manual §10.2 + §3.8.2). */
export type MissingCategory = "MCAR" | "MAR" | "MNAR";

/** The missing-value behavior primitives the authoring surface exposes
 *  (v4 operator model, ADR-012; mirrors `MISSING_BEHAVIORS` in
 *  coframe-core `operators.py`). `impute` is REMOVED — it conflated
 *  constant substitution (now `fill`, default value on the column-spec)
 *  with estimation (`skip+rescale` / `stratified`):
 *    · skip         — drop missing from the input multiset (the manual's
 *                     "exclude"); unbiased for intensive reducers.
 *    · skip+rescale — drop, then rescale ×total/observed; the unbiased
 *                     estimator of an EXTENSIVE total under MCAR.
 *    · stratified   — skip+rescale within strata then recombined; the
 *                     extensive+MAR treatment.
 *    · propagate    — mark affected output cells as carrying missingness.
 *    · fill         — replace missing with the column-spec's declared
 *                     constant. Constant substitution only, NOT estimation.
 *    · refuse       — the (op, category) cell is non-recoverable (MNAR);
 *                     refused at plan level (ADR-014), fail-closed. */
export type MissingBehavior =
  | "skip"
  | "skip+rescale"
  | "stratified"
  | "propagate"
  | "fill"
  | "refuse";

/** Missing-value treatment is a *map* from input missingness category to a
 *  behavior — never a single scalar (Manual §10.2 + §10.12). */
export type MissingBehaviorMap = Record<MissingCategory, MissingBehavior>;

/** One operator from the L1 system catalog (Manual ch.10), serialized for
 *  the Operators page, with any author override (display_name /
 *  missing_behavior) folded in. */
export interface OperatorInfo {
  /** Immutable L1 catalog name (the override key). */
  name: string;
  /** What the operator is called in this AC's surfaces (override or name). */
  display_name: string;
  name_overridden: boolean;
  kind: "reducer" | "function";
  partition_invariant: boolean;
  identity_preserving: boolean;
  is_extensive: boolean;
  order_dependent: boolean;
  description: string;
  /** The catalog's per-category default treatment (§10.2 + §10.12). */
  default_missing_behavior: MissingBehaviorMap;
  /** Effective per-category treatment (default ⊕ author override). */
  missing_behavior: MissingBehaviorMap;
  /** Only the categories the author pinned (a partial map). */
  missing_overrides: Partial<Record<MissingCategory, MissingBehavior>>;
  missing_overridden: boolean;
  /** False for functions — mappers don't carry reducer missing-value
   *  semantics (they propagate null). */
  missing_editable: boolean;
}

export interface OperatorsResponse {
  operators: OperatorInfo[];
  override_count?: number;
}

/** A mapping-derived dimension on a DimensionFamily (Shape A — see
 *  drafts/coframe_derived_dimensions_design_v0_1.md). */
export interface DerivedDimensionView {
  name: string;
  derived_from: string;
  mapping: Record<string, string>;
  default?: string | null;
  description?: string | null;
}

/** A derived-metric declaration on a MetricFamily (Reading B — see
 *  drafts/coframe_derived_metrics_design_v0_1.md). */
export interface DerivedMetricView {
  formula: string;
  inputs: string[];
  /** Pydantic also serialises the parsed AST under ``ast``; we leave
   *  it untyped here (display surfaces use ``formula`` directly). */
  ast?: unknown;
}

export interface DraftDimensionFamilyView {
  name: string;
  members: string[];
  derived_dimensions?: DerivedDimensionView[];
}

export interface DraftMetricFamilyView {
  name: string;
  derived?: DerivedMetricView | null;
}

export interface DraftResponse {
  session_id: string;
  draft_ac: {
    ac_name: string;
    ac_description?: string | null;
    dimension_families: DraftDimensionFamilyView[];
    metric_families: DraftMetricFamilyView[];
    schemas: Array<{ schema_name: string; column_specs: unknown[] }>;
    /** v2.1 §4 AC-level filter: list of dimensional-value constraints. */
    ac_filter?: Array<Record<string, unknown>>;
    /** v2.1 §3.6 / W4 L3 operator-binding overrides:
     *    catalog operator name → physical operator name. */
    operator_overrides?: Record<string, string>;
    // Other fields present but not modeled here.
    [key: string]: unknown;
  } | null;
}

// ── Discover proposals (frontend redesign v0.3) ───────────────────────
//
// The Discover step runs the deterministic inference core over the
// scoped column pool (DiscoverColumnSpecs op) and writes one proposal
// per column to the session draft. These are *proposals only* — the
// always-deterministic guess at each column's structural declaration,
// which the human ratifies in Declare. Shape mirrors `ProposalRow` in
// coframe.author.operations.discover_specs.

export interface ProposalRow {
  table: string;
  column: string;
  data_type: string;
  /** "metric" | "dimension" — Discover's first classification (§3). */
  category: string;
  /** Anchor set A. A grain key anchors on its own name (self-by-name;
   *  ADR-024), so A = [columnName] for a grain key. */
  A: string[];
  /** Measured columns M (metrics); null/[] for dimensions / grain keys
   *  (grain keys carry empty M — grain presence is a build-time invariant,
   *  not runtime MNAR; ADR-024). */
  M: string[] | null;
  /** Family reducer operator name (metrics only; null otherwise). */
  op: string | null;
  /** Default family reducer operator (§6.1). */
  reducer_operator: string | null;
  reducer_a_block: string[];
  /** Per-logical-field trust: category / anchor / missingness (+ reducer
   *  / naa for metrics). Values are Confidence strings (high/medium/low). */
  confidence: Record<string, string>;
  /** Per-logical-field provenance, same keys as `confidence`. */
  evidence: Record<string, string>;
  /** Where the proposal came from (ADR-023): "inference" (data attestation
   *  only) or "code-affirmation" (a processing-code prior — grain key,
   *  reducer, or forced-metric — shaped this row). Drives the source badge. */
  source: string;
}

/** Result of the DiscoverColumnSpecs op (POST .../ops/DiscoverColumnSpecs). */
export interface DiscoverResult {
  tables: string[];
  rows: ProposalRow[];
  cached_tables: string[];
}

// ── Code lineage (ADR-023 L2 — processing-code as a Discover source) ──
// One extracted processing-code lineage report. The ExtractLineage op reads a
// SQL/Python ETL file and recovers its output table, grain, and per-measure
// aggregate operators — the `code-affirmed` warrant. Cached on the session so
// the next Discover run feeds it in as code-affirmation priors.

export interface LineageMeasureView {
  output_column: string;
  /** Raw aggregate call the extractor read ("SUM", "last", …). */
  operator: string;
  /** Its classification (additive / extremal / snapshot / non-additive / …). */
  operator_type: string;
}

export interface LineageReportView {
  source_file: string;
  job_type: string;
  output_table: string | null;
  grain: { columns: string[]; derived_from: string } | null;
  measures: LineageMeasureView[];
}

/** Result of the ExtractLineage op (POST .../ops/ExtractLineage). */
export interface ExtractLineageResult {
  report: LineageReportView;
}

/** Persisted proposals read back via GET .../proposals. */
export interface ProposalsResponse {
  session_id: string;
  tables: string[];
  rows: ProposalRow[];
}

// ── Installation view (W2) ────────────────────────────────────────────

export interface InstallationACView {
  name: string;
  description: string | null;
  ac_filter: Array<Record<string, unknown>>;
  dimension_family_count: number;
  metric_family_count: number;
  schema_count: number;
}

export interface InstallationResponse {
  session_id: string;
  installation_id: string;
  draft_ac_name: string | null;
  draft_ac_filter: Array<Record<string, unknown>>;
  acs: InstallationACView[];
  stability_filter: {
    default_hold_off_days: number;
    overrides: Record<string, { hold_off_days: number }>;
  } | null;
}

// ── Frame-QL query (Phase 5 slice 8) ──────────────────────────────────

export interface QueryRequest {
  source: string;
  ac_name?: string | null;
}

export interface QueryFrameResponse {
  ac_name: string | null;
  columns: string[];
  rows: unknown[][];
  row_count: number;
  /** Demo Polish D4: which execution path produced this Frame.
   *   - "engine_cache": every metric served from cached / rolled-up
   *     engine state; backend NOT invoked.
   *   - "engine_mixed": some metrics from cache, some required
   *     backend execution + memoisation.
   *   - "engine_backend": engine path ran but every metric went to
   *     the backend (cold cache); results memoised for next time.
   *   - "backend": engine disabled; legacy direct-backend path.
   *   - null: pre-D4 server or non-engine path that doesn't report. */
  served_from?:
    | "engine_cache"
    | "engine_mixed"
    | "engine_backend"
    | "backend"
    | null;
}

/** Demo Polish D5: one materialised entry in the engine's manifest.
 *  Shape matches what `/sessions/{id}/engine/stats` returns. */
export interface EngineEntrySummary {
  domain: string;             // "metric" | "qm"
  dataset_id: string;
  anchor: string[];
  operator: string | null;
  row_count: number;
  byte_size: number;
  access_count: number;
  materialized_at: string;    // ISO-8601
  last_access_at: string;     // ISO-8601
}

export interface EngineStatsResponse {
  /** Engine is in the query path (registry wired + AC resolvable). The
   * engine always runs when this is true; results are identical whether
   * caching is on or off. */
  enabled: boolean;
  /** Durable caching/persistence (installation metric_engine.enabled).
   * When false the engine still runs but its cache is ephemeral —
   * discarded on restart, never written to the durable store. */
  caching?: boolean;
  installation_id: string;
  entry_count: number;
  total_byte_size: number;
  max_bytes: number | null;
  entries: EngineEntrySummary[];
}

/** Demo Polish D5 + Phase 8 F5: one promotion recommendation. */
export interface EngineRecommendation {
  dataset_id: string;
  anchor: string[];
  domain: string;
  operator: string | null;
  source_schemas: string[];
  access_count: number;
  materialized_at: string;
  age_days: number;
  hits_per_day: number;
  suggested_yaml: string;     // paste-able cache_hint stanza
}

export interface EngineRecommendationsResponse {
  enabled: boolean;
  installation_id: string;
  recommendations: EngineRecommendation[];
}

/** Structured refusal body shape (FastAPI HTTPException detail.error).
 * Surfaced via ApiError.body when the server returns 4xx with a
 * structured error. */
export interface StructuredQueryError {
  error: {
    code: string;
    message: string;
    context: Record<string, unknown>;
  };
}

// ── Connection manager (slice C4) ─────────────────────────────────────
//
// Mirrors coframe.author.http.connections route shapes. A *connection* is
// a transient handle that spans many databases; you browse it with the
// three verbs (tree / children / preview) then materialise one chosen
// database into a persistent data source (installation).

/** Normalised navigation node. ``kind`` ∈ connection / database / schema
 *  / relation / column. ``ref`` is an opaque token to pass back to
 *  children() / preview(). */
export interface ConnTreeNode {
  ref: string;
  kind: string;
  name: string;
  detail: string | null;
  has_children: boolean;
}

export interface ConnectorsResponse {
  engines: string[];
}

export interface OpenConnectionRequest {
  engine: string;
  path: string;
}

export interface ConnectResponse {
  connection_id: string;
  engine: string;
  root: ConnTreeNode;
}

export interface ConnChildrenResponse {
  children: ConnTreeNode[];
}

export interface ConnPreviewResponse {
  columns: string[];
  rows: unknown[][];
  truncated: boolean;
}

export interface CreateDataSourceRequest {
  connection_id: string;
  database: string;
  name?: string | null;
  description?: string | null;
  default_hold_off_days?: number;
  table_hold_off_overrides?: Record<string, number>;
  metric_engine_enabled?: boolean;
}

export interface CreateDataSourceResponse {
  name: string;
  installation_yaml_path: string;
  backend_type: string;
  backend_source: string;
  database: string;
  registered: boolean;
}

export interface ApiClientOptions {
  /** Base URL for the API. Defaults to "/api" (Vite proxy in dev). */
  baseUrl?: string;
  /** Optional custom fetch (for tests / SSR). */
  fetchImpl?: typeof fetch;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class CoframeApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: ApiClientOptions = {}) {
    // Resolution order matches AnalystApiClient: explicit option >
    // Vite build-time env var > dev-proxy default. In the production
    // Fly deploy the workbench backend isn't shipped, so the env var
    // typically stays "" (calls 404 visibly) until the author surface
    // ships as its own deploy.
    const envBase = (import.meta.env?.VITE_API_BASE_URL as string | undefined);
    this.baseUrl = options.baseUrl ?? envBase ?? "/api";
    this.fetchImpl = options.fetchImpl ?? fetch.bind(globalThis);
  }

  // ── Sanity ──────────────────────────────────────────────────────────

  async health(): Promise<HealthResponse> {
    return this.request<HealthResponse>("GET", "/healthz");
  }

  async listOps(): Promise<OpCatalog> {
    return this.request<OpCatalog>("GET", "/ops");
  }

  async listInstallations(): Promise<InstallationsResponse> {
    return this.request<InstallationsResponse>("GET", "/installations");
  }

  // ── Sessions ────────────────────────────────────────────────────────

  async createSession(req: CreateSessionRequest): Promise<SessionInfo> {
    return this.request<SessionInfo>("POST", "/sessions", req);
  }

  async getSession(sessionId: string): Promise<SessionInfo> {
    return this.request<SessionInfo>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}`,
    );
  }

  async getSessionLog(sessionId: string): Promise<SessionLog> {
    return this.request<SessionLog>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/log`,
    );
  }

  async getConditions(sessionId: string): Promise<ConditionsResponse> {
    return this.request<ConditionsResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/conditions`,
    );
  }

  async getDraft(sessionId: string): Promise<DraftResponse> {
    return this.request<DraftResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/draft`,
    );
  }

  async saveSession(sessionId: string): Promise<{ saved_path: string }> {
    return this.request<{ saved_path: string }>(
      "POST",
      `/sessions/${encodeURIComponent(sessionId)}/save`,
    );
  }

  async getInstallation(sessionId: string): Promise<InstallationResponse> {
    return this.request<InstallationResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/installation`,
    );
  }

  // Column pool — cached column-level extraction. The server returns the
  // session's cached column views when present (and the scope hasn't
  // changed); pass refresh=true to force a re-profile (what the "Start
  // Lattice" / manual Refresh path uses).
  async getColumns(
    sessionId: string,
    opts: { refresh?: boolean } = {},
  ): Promise<ColumnPoolResponse> {
    const qs = opts.refresh ? "?refresh=true" : "";
    return this.request<ColumnPoolResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/columns${qs}`,
    );
  }

  // The session's operator registry — the L1 system catalog (Manual
  // ch.10) merged with the author's per-operator overrides (display_name /
  // missing_behavior). Drives the Operators page (Connect & Scope).
  async getSessionOperators(sessionId: string): Promise<OperatorsResponse> {
    return this.request<OperatorsResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/operators`,
    );
  }

  // Discover proposals — the per-column structural guesses the
  // DiscoverColumnSpecs op persisted to the session draft. Read-only;
  // returns an empty rows list until Discover has run.
  async getProposals(sessionId: string): Promise<ProposalsResponse> {
    return this.request<ProposalsResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/proposals`,
    );
  }

  // Extract code lineage from a processing-code file (ADR-023 L2). Runs the
  // ExtractLineage op, which caches a `code-affirmed` LineageReport on the
  // session — the next Discover run consumes it as code-affirmation priors.
  async extractLineage(
    sessionId: string,
    path: string,
  ): Promise<RunOpResponse> {
    return this.runOp(sessionId, "ExtractLineage", { inputs: { path } });
  }

  // ── Op dispatch ─────────────────────────────────────────────────────

  async runOp(
    sessionId: string,
    opName: string,
    req: RunOpRequest = { inputs: {} },
  ): Promise<RunOpResponse> {
    return this.request<RunOpResponse>(
      "POST",
      `/sessions/${encodeURIComponent(sessionId)}/ops/${encodeURIComponent(opName)}`,
      req,
    );
  }

  // ── Frame-QL query (Phase 5 slice 8) ────────────────────────────────

  async runQuery(
    sessionId: string,
    req: QueryRequest,
  ): Promise<QueryFrameResponse> {
    return this.request<QueryFrameResponse>(
      "POST",
      `/sessions/${encodeURIComponent(sessionId)}/query`,
      req,
    );
  }

  // ── Engine surface (Demo Polish D5) ─────────────────────────────────

  async getEngineStats(sessionId: string): Promise<EngineStatsResponse> {
    return this.request<EngineStatsResponse>(
      "GET",
      `/sessions/${encodeURIComponent(sessionId)}/engine/stats`,
    );
  }

  async getEngineRecommendations(
    sessionId: string,
    params: {
      min_hits?: number;
      min_age_days?: number;
      min_hits_per_day?: number;
    } = {},
  ): Promise<EngineRecommendationsResponse> {
    const qs = new URLSearchParams();
    if (params.min_hits !== undefined)
      qs.set("min_hits", String(params.min_hits));
    if (params.min_age_days !== undefined)
      qs.set("min_age_days", String(params.min_age_days));
    if (params.min_hits_per_day !== undefined)
      qs.set("min_hits_per_day", String(params.min_hits_per_day));
    const path =
      `/sessions/${encodeURIComponent(sessionId)}/engine/recommendations` +
      (qs.toString() ? `?${qs.toString()}` : "");
    return this.request<EngineRecommendationsResponse>("GET", path);
  }

  // ── Connection manager (slice C4) ───────────────────────────────────

  async listConnectors(): Promise<ConnectorsResponse> {
    return this.request<ConnectorsResponse>("GET", "/connectors");
  }

  async openConnection(
    req: OpenConnectionRequest,
  ): Promise<ConnectResponse> {
    return this.request<ConnectResponse>("POST", "/connections", req);
  }

  async getConnectionChildren(
    connectionId: string,
    ref: string,
  ): Promise<ConnChildrenResponse> {
    const qs = new URLSearchParams({ ref });
    return this.request<ConnChildrenResponse>(
      "GET",
      `/connections/${encodeURIComponent(connectionId)}/children?${qs.toString()}`,
    );
  }

  async getConnectionPreview(
    connectionId: string,
    ref: string,
    params: { n?: number; offset?: number } = {},
  ): Promise<ConnPreviewResponse> {
    const qs = new URLSearchParams({ ref });
    if (params.n !== undefined) qs.set("n", String(params.n));
    if (params.offset !== undefined) qs.set("offset", String(params.offset));
    return this.request<ConnPreviewResponse>(
      "GET",
      `/connections/${encodeURIComponent(connectionId)}/preview?${qs.toString()}`,
    );
  }

  async createDataSource(
    req: CreateDataSourceRequest,
  ): Promise<CreateDataSourceResponse> {
    return this.request<CreateDataSourceResponse>(
      "POST",
      "/installations",
      req,
    );
  }

  // ── Internals ───────────────────────────────────────────────────────

  private async request<T>(
    method: "GET" | "POST",
    path: string,
    body?: unknown,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const init: RequestInit = {
      method,
      headers: body === undefined ? undefined : { "Content-Type": "application/json" },
      body: body === undefined ? undefined : JSON.stringify(body),
    };
    const response = await this.fetchImpl(url, init);
    let payload: unknown = undefined;
    try {
      payload = await response.json();
    } catch {
      // No JSON body — fine for 204 / error cases without one.
    }
    if (!response.ok) {
      const detail =
        typeof payload === "object" && payload !== null && "detail" in payload
          ? String((payload as { detail: unknown }).detail)
          : response.statusText;
      throw new ApiError(response.status, detail, payload);
    }
    return payload as T;
  }
}
